import axiosInstance from "@lhd/libs/axios";
import { SERVICES } from "@lhd/constants";
import {
  CreateSalaryExportParams,
  SalaryExportHistoryResponse,
  SalaryExportRecord,
  SalaryLedgerRequest,
  SalaryLedgersResponse,
} from "@lhd/features/Salary/SalaryLedgers/types";

/**
 * Fetch salary ledgers data
 */
export async function fetchSalaryLedgers(
  params: SalaryLedgerRequest,
): Promise<SalaryLedgersResponse> {
  const response = await axiosInstance.post<SalaryLedgersResponse>(
    SERVICES.SALARY_LEDGERS,
    params,
  );
  return response.data;
}

/**
 * Create salary export job (returns 202 Accepted with export record)
 * POST /api/v1/salary_exports
 */
export async function createSalaryExportJob(
  params: CreateSalaryExportParams,
): Promise<SalaryExportRecord> {
  const response = await axiosInstance.post<SalaryExportRecord>(
    SERVICES.SALARY_EXPORTS,
    params,
  );
  return response.data;
}

/**
 * Fetch export histories for the signed-in user in company
 * GET /api/v1/salary_exports?company_id=:id&page=:page
 */
export async function fetchSalaryExportHistories(
  companyId: number,
  page = 1,
): Promise<SalaryExportHistoryResponse> {
  const response = await axiosInstance.get<SalaryExportHistoryResponse>(
    SERVICES.SALARY_EXPORTS,
    {
      params: { company_id: companyId, page },
    },
  );
  return response.data;
}

/**
 * Poll status of a specific export job
 * GET /api/v1/salary_exports/:id
 */
export async function pollSalaryExportStatus(
  id: number | string,
): Promise<SalaryExportRecord> {
  const response = await axiosInstance.get<SalaryExportRecord>(
    `${SERVICES.SALARY_EXPORTS}/${id}`,
  );
  return response.data;
}

/**
 * Download the exported ZIP archive with auth headers
 * GET /api/v1/salary_exports/:id/download
 */
export async function downloadSalaryExportZip(
  id: number | string,
  fileName?: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await axiosInstance.get(
    `${SERVICES.SALARY_EXPORTS}/${id}/download`,
    {
      responseType: "blob",
      signal,
    },
  );

  if (signal?.aborted) return;

  const blob = new Blob([response.data], { type: "application/zip" });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = fileName || `salary_ledgers_${id}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

/**
 * Retry a failed or deleted export record
 * POST /api/v1/salary_exports/:id/retry
 */
export async function retrySalaryExportJob(
  id: number | string,
): Promise<SalaryExportRecord> {
  const response = await axiosInstance.post<SalaryExportRecord>(
    `${SERVICES.SALARY_EXPORTS}/${id}/retry`,
  );
  return response.data;
}

// Aliases for backward compatibility
export const createSalaryLedgerExportJob = createSalaryExportJob;
export const retrySalaryLedgerExportJob = retrySalaryExportJob;
export async function fetchSalaryLedgerExportHistories(
  companyId: number,
  page = 1,
): Promise<SalaryExportRecord[]> {
  try {
    const res = await fetchSalaryExportHistories(companyId, page);
    return res.data || [];
  } catch {
    return [];
  }
}
